import { API_CALL_FAILURE, API_CALL_REQUEST, API_CALL_SUCCESS } from 'actions/actionConstants'
import {
  FETCH_MERCHANDISE_BRANDS,
  FETCH_MERCHANDISE_BRANDS_LIST,
  FETCH_MERCHANDISE_BY_ID,
  FETCH_MERCHANDISE_CATEGORIES,
  FETCH_MERCHANDISE_CATEGORIES_LIST,
  FETCH_MERCHANDISE_HERO_PROMOS,
  FETCH_MERCHANDISE_LIST,
  FETCH_MERCHANDISE_SEARCH_FACETS,
  FETCH_MERCHANDISE_SEARCH_TYPEAHEAD,
  FETCH_RECENTLY_VIEWED_MERCHANDISE
} from 'actions/apiActionConstants'
import { arrayToObject } from 'lib/array/arrayUtils'
import { apiReducerSwitch, createAppReducer } from 'lib/redux/reducerUtils'

export const initialMerchandiseState: App.MerchandiseState = {
  fetchingMerchandise: {},
  merchandiseErrors: {},
  merchandiseLists: {},
  fetchingMerchandiseLists: {},
  merchandiseListErrors: {},
  merchandise: {},
  merchandiseCategories: {},
  fetchingMerchandiseCategories: {},
  merchandiseCategoryErrors: {},
  merchandiseHeroPromos: undefined,
  merchandiseBrands: undefined,
  merchandiseSearchFacets: {},
  recentlyViewedMerchandise: undefined,
  merchandiseSearchTypeahead: {},
  merchandiseCategoryLists: {},
  fetchingMerchandiseCategoryLists: {},
  merchandiseCategoryListErrors: {},
  merchandiseBrandLists: {},
  fetchingMerchandiseBrandLists: {},
  merchandiseBrandListErrors: {},
}

const apiCalls = apiReducerSwitch<App.MerchandiseState>({
  [FETCH_MERCHANDISE_BRANDS_LIST]: {
    request: (state, action) => ({
      fetchingMerchandiseBrandLists: {
        ...state.fetchingMerchandiseBrandLists,
        [action.key]: true,
      },
    }),
    success: (state, action) => ({
      fetchingMerchandiseBrandLists: {
        ...state.fetchingMerchandiseBrandLists,
        [action.key]: false,
      },
      merchandiseBrandLists: {
        ...state.merchandiseBrandLists,
        [action.key]: action.data,
      },
    }),
    failure: (state, action) => ({
      fetchingMerchandiseBrandLists: {
        ...state.fetchingMerchandiseBrandLists,
        [action.key]: false,
      },
      merchandiseBrandListErrors: {
        ...state.merchandiseBrandListErrors,
        [action.key]: action.error,
      },
    }),
  },
  [FETCH_MERCHANDISE_CATEGORIES_LIST]: {
    request: (state, action) => ({
      fetchingMerchandiseCategoryLists: {
        ...state.fetchingMerchandiseCategoryLists,
        [action.key]: true,
      },
    }),
    success: (state, action) => ({
      fetchingMerchandiseCategoryLists: {
        ...state.fetchingMerchandiseCategoryLists,
        [action.key]: false,
      },
      merchandiseCategoryLists: {
        ...state.merchandiseCategoryLists,
        [action.key]: action.data,
      },
    }),
    failure: (state, action) => ({
      fetchingMerchandiseCategoryLists: {
        ...state.fetchingMerchandiseCategoryLists,
        [action.key]: false,
      },
      merchandiseCategoryListErrors: {
        ...state.merchandiseCategoryListErrors,
        [action.key]: action.error,
      },
    }),
  },
  [FETCH_MERCHANDISE_SEARCH_TYPEAHEAD]: {
    request: (state, action) => ({
      merchandiseSearchTypeahead: {
        ...state.merchandiseSearchTypeahead,
        [action.key]: {
          results: undefined,
          fetching: true,
        },
      },
    }),
    success: (state, action) => ({
      merchandiseSearchTypeahead: {
        ...state.merchandiseSearchTypeahead,
        [action.key]: {
          results: action.data,
          fetching: false,
        },
      },
    }),
    failure: (state, action) => ({
      merchandiseSearchTypeahead: {
        ...state.merchandiseSearchTypeahead,
        [action.key]: {
          results: undefined,
          fetching: false,
          error: action.error,
        },
      },
    }),
  },
  [FETCH_RECENTLY_VIEWED_MERCHANDISE]: {
    request: () => ({
      recentlyViewedMerchandise: {
        merchandiseIds: [],
        fetching: true,
      },
    }),
    success: (state, action) => ({
      recentlyViewedMerchandise: {
        merchandiseIds: action.data,
        fetching: false,
      },
    }),
    failure: (_, action) => ({
      recentlyViewedMerchandise: {
        merchandiseIds: [],
        fetching: false,
        error: action.error,
      },
    }),
  },
  [FETCH_MERCHANDISE_SEARCH_FACETS]: {
    request: (state, action) => ({
      merchandiseSearchFacets: {
        ...state.merchandiseSearchFacets,
        [action.key]: {
          facets: undefined,
          fetching: true,
        },
      },
    }),
    success: (state, action) => ({
      merchandiseSearchFacets: {
        ...state.merchandiseSearchFacets,
        [action.key]: {
          facets: action.data,
          fetching: false,
        },
      },
    }),
    failure: (state, action) => ({
      merchandiseSearchFacets: {
        ...state.merchandiseSearchFacets,
        [action.key]: {
          facets: undefined,
          fetching: false,
          error: action.error,
        },
      },
    }),
  },

  [FETCH_MERCHANDISE_BRANDS]: {
    request: () => ({
      merchandiseBrands: {
        brands: {},
        fetching: true,
      },
    }),
    success: (state, action) => ({
      merchandiseBrands: {
        brands: arrayToObject(action.data, brand => brand.id),
        fetching: false,
      },
    }),

    failure: (_, action) => ({
      merchandiseBrands: {
        brands: {},
        fetching: false,
        error: action.error,
      },
    }),
  },
  [FETCH_MERCHANDISE_BY_ID]: {
    request: (state, action) => ({
      fetchingMerchandise: {
        ...state.fetchingMerchandise,
        [action.id]: true,
      },
    }),

    success: (state, action) => ({
      fetchingMerchandise: {
        ...state.fetchingMerchandise,
        [action.id]: false,
      },
      merchandise: {
        ...state.merchandise,
        [action.id]: action.data,
      },
    }),

    failure: (state, action) => ({
      fetchingMerchandise: {
        ...state.fetchingMerchandise,
        [action.id]: false,
      },
      merchandiseErrors: {
        ...state.merchandiseErrors,
        [action.id]: action.error,
      },
    }),
  },

  [FETCH_MERCHANDISE_LIST]: {
    request: (state, action) => ({
      fetchingMerchandiseLists: {
        ...state.fetchingMerchandiseLists,
        [action.key]: true,
      },
    }),

    success: (state, action) => ({
      fetchingMerchandiseLists: {
        ...state.fetchingMerchandiseLists,
        [action.key]: false,
      },
      merchandiseLists: {
        ...state.merchandiseLists,
        [action.key]: action.data,
      },
    }),

    failure: (state, action) => ({
      fetchingMerchandiseLists: {
        ...state.fetchingMerchandiseLists,
        [action.key]: false,
      },
      merchandiseListErrors: {
        ...state.merchandiseListErrors,
        [action.key]: action.error,
      },
    }),
  },
  [FETCH_MERCHANDISE_HERO_PROMOS]: {
    request: () => ({
      merchandiseHeroPromos: {
        heroPromos: [],
        fetching: true,
      },
    }),

    success: (state, action) => ({
      merchandiseHeroPromos: {
        heroPromos: action.data,
        fetching: false,
      },
    }),

    failure: (_, action) => ({
      merchandiseHeroPromos: {
        heroPromos: [],
        fetching: false,
        error: action.error,
      },
    }),
  },

  [FETCH_MERCHANDISE_CATEGORIES]: {
    request: (state, action) => ({
      fetchingMerchandiseCategories: {
        ...state.fetchingMerchandiseCategories,
        ...arrayToObject(action.keys, key => key, () => true),
      },
    }),

    success: (state, action) => ({
      fetchingMerchandiseCategories: {
        ...state.fetchingMerchandiseCategories,
        ...arrayToObject(action.keys, key => key, () => false),
      },
      merchandiseCategories: {
        ...state.merchandiseCategories,
        ...arrayToObject(action.data, (cat: App.MerchandiseCategory) => cat.id),
      },
    }),

    failure: (state, action) => ({
      fetchingMerchandiseCategories: {
        ...state.fetchingMerchandiseCategories,
        ...arrayToObject(action.keys, key => key, () => false),
      },
      merchandiseCategoryErrors: {
        ...state.merchandiseCategoryErrors,
        ...arrayToObject(action.keys, key => key, () => action.error),
      },
    }),
  },
})

export default createAppReducer<App.MerchandiseState>(initialMerchandiseState, {
  [API_CALL_REQUEST]: (state, action) => apiCalls(state, action),
  [API_CALL_FAILURE]: (state, action) => apiCalls(state, action),
  [API_CALL_SUCCESS]: (state, action) => apiCalls(state, action),
})
